<template>
  <div class="rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-gradient-to-br from-indigo-50/50 via-purple-50/20 to-white dark:from-indigo-950/25 dark:via-purple-950/15 dark:to-slate-800/90 p-5 sm:p-6 shadow-sm transition-all">
    
    <!-- Top Row: Plan Info & Action Buttons -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      
      <!-- Left: Plan Header -->
      <div class="space-y-1.5 min-w-0">
        <div class="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {{ $t('subscriptionPage.currentPlan') || 'Plano Atual' }}
        </div>
        
        <div class="flex flex-wrap items-center gap-2.5">
          <span class="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            {{ planDisplayName }}
          </span>

          <!-- Status Badge -->
          <span v-if="showCancelScheduledBadge"
                class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20">
            <span class="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
            {{ $t('subscriptionPage.cancelScheduled') || 'Cancelamento agendado' }}
          </span>
          <span v-else-if="!isFree"
                class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            {{ $t('subscriptionPage.active') || 'Ativo' }}
          </span>
          <span v-if="providerName && !isFree"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
            {{ providerName }}
          </span>
          <span v-else
                class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-200/70 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300/60 dark:border-slate-600">
            {{ $t('subscriptionPage.free') || 'Gratuito' }}
          </span>
        </div>

        <p v-if="!showCancelScheduledBadge" class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 pt-0.5">
          {{ isFree ? ($t('subscriptionPage.freeDesc') || 'Subscribe to unlock premium features and monthly credits.') : ($t('subscriptionPage.activeDesc') || 'You have full access to all premium features.') }}
        </p>
      </div>

      <!-- Right: Action Buttons -->
      <div class="flex flex-wrap sm:flex-nowrap items-center gap-2.5 shrink-0 self-start sm:self-center">
        <!-- Android e iOS: Botão Manage Subscription (redireciona para as lojas) -->
        <button v-if="showManageButton"
                type="button"
                @click="handleManage"
                class="px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 active:scale-95 text-slate-700 dark:text-slate-200 text-sm font-semibold shadow-sm transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap shrink-0">
          <ArrowTopRightOnSquareIcon class="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <span>{{ manageButtonText }}</span>
        </button>

        <!-- Web: Botão Cancel Subscription (NÃO aparece Manage Subscription, apenas Cancel Subscription) -->
        <button v-if="showCancelButton"
                type="button"
                @click="handleCancelSubscription"
                :disabled="isLoadingCancel"
                class="px-4 py-2.5 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20 hover:bg-red-100/80 dark:hover:bg-red-900/30 active:scale-95 text-red-600 dark:text-red-400 text-sm font-semibold shadow-sm transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shrink-0">
          <NoSymbolIcon class="w-4 h-4" />
          <span>{{ isLoadingCancel ? ($t('subscriptionPage.canceling') || 'Cancelando...') : ($t('subscriptionPage.cancelSubscription') || 'Cancelar Assinatura') }}</span>
        </button>

        <!-- Web, Android e iOS: Botão de Subscription (Ver Planos / Upgrade) -->
        <button v-if="isSubscriptionEnabled()"
                type="button"
                @click="openModal"
                class="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 active:scale-95 text-white text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap shrink-0">
          <SparklesIcon class="w-4 h-4" />
          <span>{{ isFree ? ($t('subscriptionPage.upgradeTitle') || 'Assinar / Upgrade') : ($t('subscriptionPage.viewPlans') || 'Ver Planos') }}</span>
        </button>
      </div>
    </div>

    <!-- Notice Card when cancellation is scheduled -->
    <div v-if="showCancelScheduledBadge"
         class="mt-4 pt-3.5 border-t border-slate-200/60 dark:border-slate-700/60 flex items-start gap-3 p-3.5 rounded-xl bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-800/40 text-amber-900 dark:text-amber-200">
      <ClockIcon class="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
      <div class="space-y-0.5">
        <p class="text-xs font-semibold text-amber-900 dark:text-amber-200">
          {{ $t('subscriptionPage.cancelScheduledTitle') || 'Cancelamento agendado' }}
        </p>
        <p class="text-xs text-amber-800/90 dark:text-amber-300/90 leading-relaxed">
          {{ $t('subscriptionPage.cancelScheduledNotice') || 'Sua assinatura continuará ativa e você terá acesso total aos recursos do plano até o término do ciclo atual.' }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { SparklesIcon } from '@heroicons/vue/24/solid';
import { ArrowTopRightOnSquareIcon, NoSymbolIcon, ClockIcon } from '@heroicons/vue/24/outline';
import { openSubscriptionModal } from '@/store/app.events';
import userStore from '@/store/user.store';
import dialogStore from '@/store/dialog.store';
import toastStore from '@/store/toast.store';
import { isNativePlatform } from '@/utils/capacitor';
import { 
  isSubscriptionEnabled, 
  manageSubscriptions, 
  subscriptionService, 
  isIapSubscription, 
  isStripeSubscription,
  getSubscriptionProvider 
} from '@/service/subscription.service';

const { t } = useI18n();
const isLoadingCancel = ref(false);

const isFree = computed(() => userStore.getters['isFree']);

const isNative = computed(() => isNativePlatform());

const isIap = computed(() => isIapSubscription());
const isStripe = computed(() => isStripeSubscription());

const providerName = computed(() => {
  const p = getSubscriptionProvider();
  if (p === 'revenuecat_play_store') return 'Google Play';
  if (p === 'revenuecat_app_store') return 'App Store';
  return null;
});

const manageButtonText = computed(() => {
  const p = getSubscriptionProvider();
  if (p === 'revenuecat_play_store') {
    return t('subscriptionPage.manageOnGooglePlay') || 'Gerenciar no Google Play';
  }
  if (p === 'revenuecat_app_store') {
    return t('subscriptionPage.manageOnAppStore') || 'Gerenciar na App Store';
  }
  return t('subscriptionPage.manageSubscription') || 'Gerenciar Assinatura';
});

const isAdmin = computed(() => {
  const role = userStore.state.userInfo?.role;
  const isAdmRole = Array.isArray(role)
    ? (role.includes('ADM') || role.includes('ADMIN'))
    : (role === 'ADM' || role === 'ADMIN');
  const tier = userStore.state.userInfo?.membershipTier || userStore.state.userInfo?.membership_tier;
  const isAdmTier = tier === 'admin_tier' || tier === 'admin' || tier === 'admin_membership';
  return isAdmRole || isAdmTier;
});

const isCancelAtPeriodEnd = computed(() => {
  return Boolean(userStore.state.userInfo?.cancelAtPeriodEnd);
});

// Botão Manage Subscription (Mobile nativo OU Web com assinatura IAP de loja mobile)
const showManageButton = computed(() => {
  if (isFree.value || isAdmin.value) return false;
  if (isNative.value) return true;
  return isIap.value;
});

// Web: Botão Cancel Subscription (APENAS para assinaturas Stripe via Web/Amazon, NUNCA para IAP de lojas mobile)
const showCancelButton = computed(() => {
  if (isNative.value) return false;
  if (isFree.value || isAdmin.value) return false;
  if (isCancelAtPeriodEnd.value) return false;
  // Se for assinatura da Google Play ou App Store, o cancelamento DEVE ser feito na loja
  if (isIap.value) return false;
  return isStripe.value;
});

// Selo de cancelamento agendado (Web e Mobile quando cancelado)
const showCancelScheduledBadge = computed(() => {
  return !isFree.value && isCancelAtPeriodEnd.value;
});

const planDisplayName = computed(() => {
  const tier = userStore.state.userInfo?.membershipTier || userStore.state.userInfo?.membership_tier;
  if (!tier || tier === 'free_membership' || tier === 'free') return 'Free';
  if (tier === 'musician_tier' || tier === 'musician') return 'The Musician';
  if (tier === 'backstage_tier' || tier === 'backstage') return 'The Backstage';
  if (tier === 'admin_tier' || tier === 'admin') return 'Admin';
  return tier.replace(/_/g, ' ');
});

const openModal = () => {
  openSubscriptionModal();
};

const handleManage = async () => {
  await manageSubscriptions();
};

const handleCancelSubscription = async () => {
  const confirmed = await dialogStore.dispatch('confirm', {
    title: t('subscriptionPage.confirmCancelTitle') || 'Cancelar Assinatura',
    message: t('subscriptionPage.confirmCancelMessage') || 'Tem certeza de que deseja cancelar sua assinatura? Você continuará tendo acesso a todos os benefícios até o final do período de faturamento.'
  });

  if (!confirmed) {
    return;
  }

  try {
    isLoadingCancel.value = true;
    await subscriptionService.cancelSubscription({ immediately: false });

    // Atualização otimista e reativa imediata no store para sumir o botão e exibir o selo sem delay
    if (userStore.state.userInfo) {
      userStore.commit('INFO', {
        ...userStore.state.userInfo,
        cancelAtPeriodEnd: true
      });
    }

    toastStore.dispatch('addMessage', {
      message: t('subscriptionPage.cancelSuccess') || 'Assinatura cancelada com sucesso. O seu acesso permanecerá ativo até o final do período faturado.',
      icon: 'success',
      ttl: 5000
    });
    await userStore.dispatch('updateBasicInfo');
  } catch (error) {
    toastStore.dispatch('addMessage', {
      message: error.message || t('subscriptionPage.cancelError') || 'Erro ao cancelar assinatura. Tente novamente mais tarde.',
      icon: 'error',
      ttl: 5000
    });
  } finally {
    isLoadingCancel.value = false;
  }
};
</script>
